import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth, useCart } from '@/App';
import { CheckCircle, Package, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { token } = useAuth();
  const { fetchCart } = useCart();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/checkout/status/${sessionId}`, {
          headers,
          withCredentials: true
        });

        const data = response.data;
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          // Refresh cart to clear it
          await fetchCart();
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Error checking payment status:', error);
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      }
    };

    pollPaymentStatus();
  }, [sessionId, token, fetchCart]);

  if (status === 'checking') {
    return (
      <div className="pt-20 min-h-screen flex flex-col items-center justify-center" data-testid="checkout-checking">
        <Loader2 className="w-16 h-16 text-[#FF3B30] animate-spin mb-6" />
        <h2 className="heading-display text-2xl text-white mb-4">Processing Payment...</h2>
        <p className="text-white/60">Please wait while we confirm your order.</p>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="pt-20 min-h-screen flex flex-col items-center justify-center" data-testid="checkout-success">
        <CheckCircle className="w-20 h-20 text-green-500 mb-6" />
        <h2 className="heading-display text-3xl md:text-4xl text-white mb-4 text-center">
          Order Confirmed!
        </h2>
        <p className="text-white/60 text-center max-w-md mb-8">
          Thank you for your purchase. You will receive an email confirmation shortly.
        </p>
        
        {paymentData && (
          <div className="bg-[#0A0A0A] border border-[#27272A] p-6 mb-8 w-full max-w-md">
            <h3 className="font-barlow font-bold text-lg text-white mb-4">Order Details</h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-white/60">Amount Paid</span>
                <span className="text-white font-medium">
                  ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-white/60">Status</span>
                <span className="text-green-500 font-medium">Paid</span>
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/dashboard" data-testid="view-orders-link">
            <Button className="btn-secondary flex items-center gap-2">
              <Package className="w-5 h-5" />
              View Orders
            </Button>
          </Link>
          <Link to="/products" data-testid="continue-shopping-link">
            <Button className="btn-primary flex items-center gap-2">
              Continue Shopping
              <ArrowRight className="w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="pt-20 min-h-screen flex flex-col items-center justify-center" data-testid="checkout-expired">
        <h2 className="heading-display text-3xl text-white mb-4">Session Expired</h2>
        <p className="text-white/60 text-center max-w-md mb-8">
          Your checkout session has expired. Please try again.
        </p>
        <Link to="/cart">
          <Button className="btn-primary">Return to Cart</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen flex flex-col items-center justify-center" data-testid="checkout-error">
      <h2 className="heading-display text-3xl text-white mb-4">Something Went Wrong</h2>
      <p className="text-white/60 text-center max-w-md mb-8">
        We couldn't verify your payment. Please check your email or contact support.
      </p>
      <Link to="/cart">
        <Button className="btn-primary">Return to Cart</Button>
      </Link>
    </div>
  );
}
